import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'


import { get, post } from '@/lib/api'

import { employeeKeys } from './use-employees'

import type { CompanyRole } from './use-employees'
import type { FunctionRole, DeveloperGroup, EmployeeStatus } from '@company/shared'

// ==================== Types ====================


export type HRTeamMemberType = 'developer' | 'function'

// Re-export shared types for convenience
export type { FunctionRole, DeveloperGroup }

// 入职输入
export interface OnboardingInput {
  basicInfo: {
    name: string
    email: string
    phone?: string
    avatar?: string
    hireDate: string
  }
  organization?: {
    departmentId: string
    positionId: string
    managerId?: string
    role: CompanyRole
  }
  teams?: Array<{
    teamId: string
    memberType: HRTeamMemberType
    developerGroup?: DeveloperGroup
    functionRole?: FunctionRole
    isLeader?: boolean
  }>
  salary?: {
    baseSalary: number
    allowance?: number
    effectiveDate: string
  }
  leaveBalances?: Array<{
    type: string
    total: number
  }>
}

// 入职结果
export interface OnboardingResult {
  employee: {
    id: string
    employeeNo: string
    name: string
    email: string
    status: EmployeeStatus
    hireDate: string
  }
  employeeNo: string
  teamMemberships: Array<{
    id: string
    teamId: string
    employeeId: string
    memberType: string
    isLeader: boolean
  }>
  salary?: {
    id: string
    baseSalary: number
    allowance: number
  } | null
  leaveBalances: Array<{
    id: string
    type: string
    total: number
    used: number
  }>
  welcomeEmailSent: boolean
}

// 离职输入
export interface OffboardingInput {
  resignDate: string
  reason?: string
  taskHandover: Record<string, string>
  sendNotification?: boolean
}

// 离职结果
export interface OffboardingResult {
  employee: {
    id: string
    name: string
    status: EmployeeStatus
    resignDate: string | null
  }
  archivedTeamMemberships: number
  reassignedTasks: number
  cancelledLeaveRequests: number
  escalatedApprovals: number
  summaryReport: {
    employeeId: string
    employeeName: string
    resignDate: string
    reason?: string
    totalWorkDays: number
    archivedTeams: string[]
    reassignedTaskIds: string[]
    cancelledLeaveIds: string[]
  }
}


// 员工全景数据
export interface EmployeeProfile {
  basic: {
    id: string
    employeeNo: string
    name: string
    email: string
    phone?: string | null
    avatar?: string | null
    status: EmployeeStatus
    hireDate: string
    resignDate?: string | null
  }
  organization: {
    department?: { id: string; name: string } | null
    position?: { id: string; name: string; level: number | null } | null
    manager?: { id: string; name: string } | null
    role: CompanyRole
  }
  teams: Array<{
    id: string
    name: string
    memberType: string
    developerGroup?: string | null
    functionRole?: string | null
    isLeader: boolean
    joinDate: string
    endDate?: string | null
    isActive: boolean
  }>
  projects: Array<{
    id: string
    name: string
    projectNo: string
    status: string
  }>
  taskStats: {
    pending: number
    inProgress: number
    completed: number
    total: number
  }
  leave: {
    balances: Array<{
      id: string
      type: string
      total: number
      used: number
    }>
    recentRequests: Array<{
      id: string
      type: string
      startDate: string
      endDate: string
      status: string
    }>
    currentlyOnLeave: boolean
  }
  salary?: {
    current: {
      id: string
      baseSalary: number
      allowance: number
      effectiveDate: string
    } | null
    history: Array<{
      id: string
      baseSalary: number
      allowance: number
      effectiveDate: string
    }>
  }
  timeline: EmployeeEvent[]
}

// 员工事件
export interface EmployeeEvent {
  id: string
  employeeId: string
  type: string
  description: string
  metadata?: Record<string, unknown> | null
  createdAt: string
  createdBy: string | null
}

// 状态更新输入
export interface EmployeeUpdateStatusInput {
  status: EmployeeStatus
}

// ==================== Query Keys ====================

export const lifecycleKeys = {
  all: ['employee-lifecycle'] as const,
  profiles: () => [...lifecycleKeys.all, 'profile'] as const,
  profile: (id: string) => [...lifecycleKeys.profiles(), id] as const,
  stats: () => [...lifecycleKeys.all, 'stats'] as const,
  timelines: () => [...lifecycleKeys.all, 'timeline'] as const,
  timeline: (id: string) => [...lifecycleKeys.timelines(), id] as const,
}


// ==================== Hooks ====================

/**
 * 执行入职流程
 */
export function useOnboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: OnboardingInput) =>
      post<OnboardingResult>('/hr/employees/lifecycle/onboard', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.active() })
      void queryClient.invalidateQueries({ queryKey: lifecycleKeys.stats() })
    },
  })
}

/**
 * 执行离职流程
 */
export function useOffboarding() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OffboardingInput }) =>
      post<OffboardingResult>(`/hr/employees/lifecycle/${id}/offboard`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.active() })
      void queryClient.invalidateQueries({ queryKey: lifecycleKeys.profile(id) })
      void queryClient.invalidateQueries({ queryKey: lifecycleKeys.stats() })
    },
  })
}

/**
 * 获取员工全景数据
 */
export function useEmployeeProfile(id: string) {
  return useQuery({
    queryKey: lifecycleKeys.profile(id),
    queryFn: () => get<EmployeeProfile>(`/hr/employees/lifecycle/${id}/profile`),
    enabled: !!id,
  })
}

/**
 * 更新员工状态
 */
export function useUpdateEmployeeStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EmployeeUpdateStatusInput }) =>
      post<null>(`/hr/employees/lifecycle/${id}/status`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: lifecycleKeys.profile(id) })
      void queryClient.invalidateQueries({ queryKey: lifecycleKeys.stats() })
    },
  })
}

/**
 * 获取员工时间线
 */
export function useEmployeeTimeline(id: string) {
  return useQuery({
    queryKey: lifecycleKeys.timeline(id),
    queryFn: () => get<EmployeeEvent[]>(`/hr/employees/lifecycle/${id}/timeline`),
    enabled: !!id,
  })
}
